import type { ReactNode, Ref } from 'react';
import {
  Tab as AriaTab,
  TabList as AriaTabList,
  TabPanel as AriaTabPanel,
  Tabs as AriaTabs,
  type TabListProps,
  type TabPanelProps,
  type TabProps,
  type TabsProps,
} from 'react-aria-components';
import styles from './Tabs.module.css';

// Tabs wrapper (react-aria-components): role="tablist"/role="tab"/role=
// "tabpanel", aria-selected, arrow-key navigation, focus follows selection
// (§4.1). Use the compound pieces or the convenience wrapper below.
const join = (...names: (string | undefined)[]) => names.filter(Boolean).join(' ');

// A layout can add a class (a height, say) without replacing the base styling.
export const Tabs = ({
  className,
  ...props
}: Omit<TabsProps, 'className'> & { className?: string }) => (
  <AriaTabs {...props} className={join(styles.tabs, className)} />
);
export const TabList = <T extends object>(props: TabListProps<T>) => (
  <AriaTabList {...props} className={styles.tabList} />
);
export const Tab = (props: TabProps & { ref?: Ref<HTMLDivElement> }) => (
  <AriaTab {...props} className={styles.tab} />
);
export const TabPanel = ({
  className,
  ...props
}: Omit<TabPanelProps, 'className'> & { className?: string }) => (
  <AriaTabPanel {...props} className={join(styles.tabPanel, className)} />
);

export interface SimpleTabsProps {
  'aria-label'?: string;
  items: { id: string; label: string }[];
  children?: ReactNode;
}

/** Convenience wrapper for labelled static tab sets. */
export function SimpleTabs({ items, children, ...rest }: SimpleTabsProps) {
  return (
    <Tabs {...rest}>
      <TabList items={items}>{(item) => <Tab id={item.id}>{item.label}</Tab>}</TabList>
      {children}
    </Tabs>
  );
}
